import type { AiPrice } from './ai';
import { formatNumber, plural } from './format';
import { zonedParts } from './home';
import { normaliseTerms, PROFILE_LIMITS } from './profiles';
import type { AiProvider, Effort, ValueSource } from './types';

// AI Finder: the shapes the server and the web app share, plus the pure logic both sides need
// (progress stages, cost estimates, the normalising that turns model output into clean suggestions).

export type FinderMode = 'describe' | 'like' | 'filters' | 'fill' | 'people';
export type SearchMode = Exclude<FinderMode, 'fill' | 'people'>;
export type SearchStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'stopped';
export type StageKey = 'prepare' | 'search' | 'read' | 'people' | 'check';

/** The modes the AI Finder screen offers; key people are looked up from a company's record. */
export const FINDER_MODES: FinderMode[] = ['describe', 'like', 'filters', 'fill'];
export const SEARCH_MODES: SearchMode[] = ['describe', 'like', 'filters'];

export const MODE_LABELS: Record<FinderMode, string> = {
  describe: 'Describe',
  like: 'Similar',
  filters: 'Filters',
  fill: 'Fill details',
  people: 'Key people',
};

/** How many suggestions a search may ask for. */
export const COUNT_OPTIONS = [5, 10, 15, 20, 25, 30] as const;
export const DEFAULT_COUNT = 15;
export const MAX_COUNT = 30;
/** The most people a suggestion names. */
export const MAX_PEOPLE = 4;

/* ---------- search parameters ---------- */

export type LikeTrait = 'industry' | 'size' | 'location' | 'needs' | 'about';
export const LIKE_TRAITS: LikeTrait[] = ['industry', 'size', 'location', 'needs', 'about'];

export const STAFF_BANDS = [
  { id: '1-50', label: '1–50', min: 1, max: 50 },
  { id: '50-100', label: '50–100', min: 50, max: 100 },
  { id: '100-500', label: '100–500', min: 100, max: 500 },
  { id: '500-1000', label: '500–1,000', min: 500, max: 1000 },
  { id: '1000+', label: '1,000+', min: 1000, max: null },
] as const;
export type StaffBand = (typeof STAFF_BANDS)[number]['id'];

export interface DescribeParams {
  count: number;
}

export interface LikeParams {
  companyId: string;
  /** Kept so the search still reads well if the company is later renamed or deleted. */
  companyName: string;
  traits: LikeTrait[];
  note: string;
  count: number;
}

export interface FilterParams {
  industry: string;
  location: string;
  staff: StaffBand | '';
  foundedFrom: number | null;
  foundedTo: number | null;
  keywords: string;
  count: number;
  /** Leave out companies already in Clientbook rather than flagging them. */
  excludeKnown: boolean;
}

export interface FillParams {
  /** What the run looks up. Runs from before people could be filled looked up companies. */
  target?: FillTarget;
  companyIds?: string[];
  personIds?: string[];
  fields: FillField[];
  automatic: boolean;
  /** A run on the people at one company, started from that company's record. */
  companyId?: string;
  companyName?: string;
}

/** Whether a fill run looks up companies or people. */
export function fillRunTarget(params: Pick<FillParams, 'target'>): FillTarget {
  return params.target === 'person' ? 'person' : 'company';
}

/** The records a fill run looks up. */
export function fillRunIds(params: Pick<FillParams, 'target' | 'companyIds' | 'personIds'>): string[] {
  const ids = fillRunTarget(params) === 'person' ? params.personIds : params.companyIds;
  return Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : [];
}

/** A look-up of the key people at a company already in Clientbook. */
export interface PeopleParams {
  companyIds: string[];
  /** Kept so the look-up still reads well if a company is later renamed or deleted, in the order of `companyIds`. */
  companyNames: string[];
  /** Where it started: a company's record, a selection in the Companies list, or the automatic look-up of new companies. */
  source: PeopleSource;
}

export type PeopleSource = 'record' | 'list' | 'automatic';

/** The most companies one look-up of key people covers. */
export const PEOPLE_MAX_COMPANIES = 25;

/** Where one company of a key-people look-up stands. */
export type PeopleCompanyOutcome = { state: 'looking' } | { state: 'done'; found: number } | { state: 'failed'; error: string };

export type SearchParams = DescribeParams | LikeParams | FilterParams | FillParams | PeopleParams;

/* ---------- fill ---------- */

/** The kinds of record the AI fills in. */
export type FillTarget = 'company' | 'person';

export const COMPANY_FILL_FIELDS = [
  'domain', 'website', 'industry', 'staff', 'founded', 'location', 'linkedin', 'phone', 'description',
  'summary', 'market', 'clients', 'strengths', 'lacks',
] as const;
export type CompanyFillField = (typeof COMPANY_FILL_FIELDS)[number];

export const PERSON_FILL_FIELDS = ['title', 'linkedin', 'location', 'phone'] as const;
export type PersonFillField = (typeof PERSON_FILL_FIELDS)[number];

/** Every detail the AI fills in, on either kind of record. */
export const FILL_FIELDS = [...COMPANY_FILL_FIELDS, 'title'] as const;
export type FillField = CompanyFillField | PersonFillField;

export const FILL_FIELDS_FOR = { company: COMPANY_FILL_FIELDS, person: PERSON_FILL_FIELDS } as const satisfies Record<FillTarget, readonly FillField[]>;

export function isFillField(target: FillTarget, field: string): field is FillField {
  return (FILL_FIELDS_FOR[target] as readonly string[]).includes(field);
}

/** The profile details among them; strengths and gaps are lists of capabilities. */
export const PROFILE_FILL_FIELDS = ['summary', 'market', 'clients', 'strengths', 'lacks'] as const satisfies readonly CompanyFillField[];
export const LIST_FILL_FIELDS: readonly FillField[] = ['strengths', 'lacks'];

/** A detail as fill reads, proposes and saves it. */
export type FillFieldValue = string | number | string[];

export const FILL_FIELD_LABELS: Record<FillField, string> = {
  title: 'Job title',
  domain: 'Domain',
  website: 'Website',
  industry: 'Industry',
  staff: 'Staff count',
  founded: 'Founded',
  location: 'Location',
  linkedin: 'LinkedIn',
  phone: 'Phone',
  description: 'Description',
  summary: 'What they do',
  market: 'Market',
  clients: 'Sells to',
  strengths: 'Strengths',
  lacks: 'Gaps',
};

/** The company details an AI Finder search looks up; each source it read is tied to the ones it gave. */
export const FOUND_FIELDS = [
  'domain', 'website', 'location', 'industry', 'staff', 'founded', 'linkedin', 'description',
  'summary', 'market', 'clients', 'strengths', 'lacks',
] as const satisfies readonly CompanyFillField[];
export type FoundField = (typeof FOUND_FIELDS)[number];

export interface FillSetting {
  /** Look up gaps on companies added in the last few days, once each. */
  companies: { enabled: boolean; fields: CompanyFillField[] };
  /** The same for people. */
  people: { enabled: boolean; fields: PersonFillField[] };
  /** Look up the key people, once, at each company added in the last few days that has nobody on record. */
  keyPeople: { enabled: boolean };
}

export const FILL_SETTING_KEY = 'finder.fill';
export const DEFAULT_FILL_SETTING: FillSetting = {
  companies: {
    enabled: true,
    fields: ['domain', 'website', 'industry', 'staff', 'founded', 'location', 'linkedin', 'description', 'summary', 'market', 'clients', 'strengths', 'lacks'],
  },
  people: { enabled: true, fields: ['title', 'linkedin', 'location'] },
  keyPeople: { enabled: true },
};

/**
 * The team setting as saved, read with today's defaults. Settings saved before people could be filled held the
 * company part alone, at the top level. A team that saved the setting before key people were looked up automatically
 * gets that the way it chose the automatic fill of companies, so nobody starts paying for it unasked.
 */
export function readFillSetting(saved: unknown): FillSetting {
  const raw = (saved && typeof saved === 'object' ? saved : {}) as Record<string, unknown>;
  const part = <F extends FillField>(value: unknown, fields: readonly F[], fallback: { enabled: boolean; fields: F[] }) => {
    const v = (value && typeof value === 'object' ? value : {}) as { enabled?: unknown; fields?: unknown };
    const chosen = Array.isArray(v.fields) ? fields.filter((f) => (v.fields as unknown[]).includes(f)) : [];
    return { enabled: typeof v.enabled === 'boolean' ? v.enabled : fallback.enabled, fields: chosen.length ? chosen : fallback.fields };
  };
  const companies = part(raw.companies ?? raw, COMPANY_FILL_FIELDS, DEFAULT_FILL_SETTING.companies);
  const keyPeople = (raw.keyPeople && typeof raw.keyPeople === 'object' ? raw.keyPeople : {}) as { enabled?: unknown };
  return {
    companies,
    people: part(raw.people, PERSON_FILL_FIELDS, DEFAULT_FILL_SETTING.people),
    keyPeople: { enabled: typeof keyPeople.enabled === 'boolean' ? keyPeople.enabled : saved && typeof saved === 'object' ? companies.enabled : DEFAULT_FILL_SETTING.keyPeople.enabled },
  };
}

/** How far back the automatic fill looks for new companies and people. */
export const FILL_RECENT_DAYS = 3;
/** The most companies or people one fill run looks up. */
export const FILL_MAX_RECORDS = 25;
/** The most proposed values one accept, reject or undo request carries. */
export const FILL_DECIDE_MAX = 300;

export type FillValueStatus = 'new' | 'accepted' | 'rejected';

export interface FillValue {
  value: FillFieldValue;
  source: string;
  sourceUrl: string | null;
  uncertain: boolean;
  status: FillValueStatus;
  /** The record's value before an accepted proposal replaced it, so undo can put it back. */
  previous?: FillFieldValue | null;
  /** The value as the record saved it on accept, so undo can tell whether anyone changed it since. */
  applied?: FillFieldValue | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
}

/* ---------- what the API returns ---------- */

export interface UserRef {
  id: string;
  name: string;
  hue: number;
}

export interface FinderSearchSummary {
  id: string;
  mode: FinderMode;
  title: string;
  status: SearchStatus;
  progress: number;
  step: StageKey | null;
  provider: AiProvider | null;
  model: string | null;
  modelName: string | null;
  effort: Effort | null;
  createdBy: UserRef | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  stoppedBy: UserRef | null;
  stoppedAt: string | null;
  resultCount: number;
  approvedCount: number;
  flaggedCount: number;
  costUsd: number;
}

export interface FinderPerson {
  name: string;
  title: string | null;
  /** A work address, only when a source published it. */
  email: string | null;
  linkedin: string | null;
  /** The page the person was found on, when there is no LinkedIn profile. */
  source: string | null;
  /** Someone already in Clientbook with the same name at that company, or the same address. */
  existing: { id: string; name: string } | null;
}

export interface FinderSource {
  url: string;
  title: string | null;
  /** The details of the suggestion this page gave. Suggestions found before sources were tied to details have none. */
  details?: FoundField[];
}

export interface FinderResult {
  id: string;
  position: number;
  name: string;
  domain: string | null;
  website: string | null;
  location: string | null;
  industry: string | null;
  staff: number | null;
  founded: number | null;
  linkedin: string | null;
  description: string | null;
  /** The company's profile, as the search found it. */
  summary: string | null;
  market: string | null;
  clients: string | null;
  strengths: string[];
  lacks: string[];
  why: string | null;
  match: number | null;
  people: FinderPerson[];
  sources: FinderSource[];
  /** The logo found on the suggestion's website, as a logo id. */
  logo: string | null;
  /** "domain": the company is already in Clientbook. "name": a company with the same name exists. */
  duplicate: { id: string; name: string; reason: 'domain' | 'name'; openDeal: string | null } | null;
  status: 'new' | 'approved' | 'dismissed';
  createdCompany: { id: string; name: string } | null;
  /** The company already in Clientbook that the suggestion's people and details were added to. */
  addedTo: { id: string; name: string } | null;
  /** How many empty details of that company the suggestion filled in. */
  addedDetails: number;
  createdPeople: number;
  decidedBy: UserRef | null;
  decidedAt: string | null;
}

/** What adding a suggestion to the company already in Clientbook would add, worked out against that company now. */
export interface FinderAddPreview {
  company: { id: string; name: string; hue: number; logo: string | null };
  /** Everyone the suggestion names, in its order; `existing` is someone already on record now. */
  people: (FinderPerson & { index: number })[];
  /** The details the company has no value for that the suggestion gives, with the pages they came from. */
  details: { field: FoundField; value: FillFieldValue; sources: ValueSource[] }[];
}

export interface FinderApprovedCompany {
  id: string;
  name: string;
  hue: number;
  logo: string | null;
  industry: string | null;
  staff: number | null;
  location: string | null;
  openValue: number;
  openCount: number;
}

export interface FinderApprovedPerson {
  id: string;
  name: string;
  hue: number;
  title: string | null;
  source: string | null;
}

export interface FinderSearchDetail extends FinderSearchSummary {
  query: string;
  params: Record<string, unknown>;
  error: string | null;
  watchers: UserRef[];
  results: FinderResult[];
  approved: { companies: FinderApprovedCompany[]; people: FinderApprovedPerson[] };
  mainCurrency: string;
}

export interface FinderSearchPage {
  items: FinderSearchSummary[];
  nextCursor: string | null;
}

export interface FinderCompanyOption {
  id: string;
  name: string;
  hue: number;
  logo: string | null;
  domain: string | null;
  industry: string | null;
  staff: number | null;
  location: string | null;
  description: string | null;
  market: string | null;
  strengths: string[];
  lacks: string[];
  customer: boolean;
  deals: string[];
  gaps: FillField[];
}

export interface FinderPersonOption {
  id: string;
  name: string;
  hue: number;
  title: string | null;
  company: string | null;
  /** The person has a company, an email address or a LinkedIn profile, so the AI can tell them apart from namesakes. */
  identifiable: boolean;
  gaps: PersonFillField[];
}

/** The people at a company, for filling in their details from the company's AI review. */
export interface CompanyPeopleFill {
  company: { id: string; name: string };
  /** AI is connected, so a lookup can start. */
  connected: boolean;
  /** The details the team's automatic fill looks up on people, which a lookup from here starts with. */
  fields: PersonFillField[];
  /** Everyone at the company, by name, each with the proposals waiting on their record. */
  people: (FinderPersonOption & { waiting: number })[];
  /** Lookups started from the company that are still running, each with how many people it looks up. */
  running: (FinderSearchSummary & { people: number })[];
}

/** A lookup on everyone at a company, as started: its runs, and how many people it covers and skipped. */
export interface CompanyFillStarted {
  company: { id: string; name: string };
  searches: string[];
  lookedUp: number;
  complete: number;
  unidentifiable: number;
}

/** What a lookup on everyone at a company covers for the chosen details. */
export interface CompanyFillPlan {
  /** The people to look up: missing a chosen detail, and someone the AI can tell apart from namesakes. */
  lookups: string[];
  /** People who have every chosen detail already. */
  complete: number;
  /** People missing a chosen detail with nothing for the AI to identify them by. */
  unidentifiable: number;
}

export interface FillRecordRef {
  type: FillTarget;
  id: string;
  name: string;
  hue: number;
  /** A company's logo id. */
  logo: string | null;
  /** A person's company. */
  company: string | null;
}

export interface FillProposalRow {
  resultId: string;
  searchId: string;
  record: FillRecordRef;
  field: FillField;
  /** The record's value now. */
  current: FillFieldValue | null;
  /** The model that proposed the value. */
  model: string | null;
  proposal: FillValue;
}

export interface FillOverview {
  rows: FillProposalRow[];
  pending: number;
  reviewed: number;
  /** Companies and people with a value on the list. */
  records: number;
  costUsd: number;
  model: string | null;
  effort: Effort | null;
  running: FinderSearchSummary[];
  lastRun: FinderSearchSummary | null;
}

export interface FinderMeta {
  connected: boolean;
  providers: AiProvider[];
  defaults: { finder: { provider: AiProvider; model: string; modelName: string; effort: Effort } | null; fill: { provider: AiProvider; model: string; modelName: string; effort: Effort } | null };
  month: { total: number; finder: number; fill: number; runs: number; byPerson: { user: UserRef | null; costUsd: number }[] };
  fill: FillSetting;
  gaps: { companies: number; people: number };
  industries: string[];
  locations: string[];
}

/* ---------- progress ---------- */

export interface Stage {
  key: StageKey;
  label: string;
}

/** The steps a search walks through, in order, with the wording the running panel shows. */
export function stagesFor(mode: FinderMode, ctx: { companyName?: string | null; companies?: number; people?: number } = {}): Stage[] {
  if (mode === 'people') {
    return [
      { key: 'prepare', label: `Reading ${ctx.companyName || 'the company'} and the people on record` },
      { key: 'people', label: 'Searching the web for key people' },
      { key: 'check', label: 'Leaving out people already in Clientbook' },
    ];
  }
  if (mode === 'fill') {
    const what = ctx.people ? recordCount('person', ctx.people) : ctx.companies ? recordCount('company', ctx.companies) : null;
    return [
      { key: 'prepare', label: 'Listing the missing details' },
      { key: 'search', label: what ? `Searching the web for ${what}` : 'Searching the web' },
      { key: 'check', label: 'Checking values against each record' },
    ];
  }
  const first =
    mode === 'like'
      ? `Reading ${ctx.companyName || 'the company'} and its deals`
      : mode === 'filters'
        ? 'Turning the filters into a search'
        : 'Understanding the description';
  return [
    { key: 'prepare', label: first },
    { key: 'search', label: 'Searching the web for matching companies' },
    { key: 'read', label: 'Reading up on each company' },
    { key: 'people', label: 'Finding key people at each company' },
    { key: 'check', label: 'Checking for duplicates and fetching logos' },
  ];
}

/** The share of the progress bar each stage covers. */
export const STAGE_BANDS: Record<StageKey, [number, number]> = {
  prepare: [0, 6],
  search: [6, 35],
  read: [35, 68],
  people: [68, 90],
  check: [90, 100],
};

/**
 * Progress inside a stage while its AI call is out: it moves fast at first and slows as the expected time passes,
 * so the bar keeps moving without ever claiming the stage is done.
 */
export function stageProgress(stage: StageKey, elapsedMs: number, expectedMs: number, band: [number, number] = STAGE_BANDS[stage]): number {
  const [start, end] = band;
  const span = end - start;
  if (elapsedMs <= 0 || expectedMs <= 0) return start;
  const share = 1 - Math.exp(-elapsedMs / expectedMs);
  return Math.min(end - 1, Math.max(start, Math.floor(start + span * 0.95 * share)));
}

/** Where each step stands for the running panel's checklist. */
export function stageStates(stages: Stage[], current: StageKey | null, status: SearchStatus): ('done' | 'active' | 'todo')[] {
  if (status === 'succeeded') return stages.map(() => 'done');
  if (status === 'queued' || !current) return stages.map((_, i) => (i === 0 && status === 'queued' ? 'active' : 'todo'));
  const at = stages.findIndex((s) => s.key === current);
  return stages.map((_, i) => (at < 0 ? 'todo' : i < at ? 'done' : i === at ? (status === 'running' ? 'active' : 'todo') : 'todo'));
}

/* ---------- estimates ---------- */

export interface Estimate {
  low: number;
  high: number;
  seconds: number;
}

const OUTPUT_BY_EFFORT: Record<Effort, number> = { low: 1500, medium: 3500, high: 7000 };
const SECONDS_BY_EFFORT: Record<Effort, number> = { low: 0.6, medium: 1, high: 1.6 };
/** Roughly what one web search adds to the prompt once its results are read. */
const TOKENS_PER_SEARCH = 3500;

/** How many web searches each call of a search may use. */
export function searchBudget(count: number): { candidates: number; details: number } {
  const n = Math.max(1, Math.min(MAX_COUNT, count));
  return { candidates: Math.min(8, 3 + Math.ceil(n / 5)), details: Math.min(15, 2 + Math.ceil(n / 2)) };
}

export const FILL_SEARCHES_PER_COMPANY = 3;
/** Web searches for the key people at one suggestion. */
export const PEOPLE_SEARCHES_PER_COMPANY = 3;
/** How many companies a search looks up people at side by side. */
export const PEOPLE_LOOKUPS_AT_ONCE = 3;

function callCost(price: AiPrice, input: number, output: number, searches: number): number {
  return (input * price.input + output * price.output) / 1_000_000 + (searches * price.webSearchPer1k) / 1000;
}

/** A price range and duration for a search before it runs, from list prices and typical token use. */
export function estimateSearch(input: { mode: FinderMode; count: number; companies?: number; effort: Effort; price: AiPrice }): Estimate {
  const { price, effort } = input;
  const out = OUTPUT_BY_EFFORT[effort];
  if (input.mode === 'fill') {
    const n = Math.max(1, input.companies ?? 1);
    const per = (s: number) => callCost(price, 1200 + s * TOKENS_PER_SEARCH, out * 0.4 + 300, s);
    return { low: per(1) * n, high: per(FILL_SEARCHES_PER_COMPANY) * n, seconds: Math.round(n * 12 * SECONDS_BY_EFFORT[effort]) };
  }
  const n = Math.max(1, Math.min(MAX_COUNT, input.count));
  const budget = searchBudget(n);
  const at = (share: number) => {
    const s1 = Math.max(1, Math.round(budget.candidates * share));
    const s2 = Math.max(1, Math.round(budget.details * share));
    const first = callCost(price, 1500 + s1 * TOKENS_PER_SEARCH, out + n * 60, s1);
    const second = callCost(price, 2500 + s2 * TOKENS_PER_SEARCH, out + n * 260, s2);
    return first + second;
  };
  return { low: at(0.5), high: at(1), seconds: Math.round((20 + n * 2.5) * SECONDS_BY_EFFORT[effort]) };
}

/** The same for the step that looks up the key people at each suggestion, one company per call, a few at once. */
export function estimatePeople(input: { companies: number; effort: Effort; price: AiPrice }): Estimate {
  const n = Math.max(0, input.companies);
  const out = OUTPUT_BY_EFFORT[input.effort];
  const per = (s: number) => callCost(input.price, 1800 + s * TOKENS_PER_SEARCH, out * 0.4 + 400, s);
  const rounds = Math.ceil(n / PEOPLE_LOOKUPS_AT_ONCE);
  return { low: per(1) * n, high: per(PEOPLE_SEARCHES_PER_COMPANY) * n, seconds: Math.round(rounds * 15 * SECONDS_BY_EFFORT[input.effort]) };
}

/* ---------- normalising model output ---------- */

/** "https://www.Acme.com/about" → "acme.com". Null for anything that is not a domain. */
export function cleanDomain(input: string | null | undefined): string | null {
  if (!input) return null;
  let v = input.trim().toLowerCase();
  if (v.includes('@')) v = v.slice(v.lastIndexOf('@') + 1);
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, '').replace(/^www\./, '');
  v = v.split(/[/?#:]/)[0] ?? '';
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v) ? v : null;
}

/** A web address with its scheme, or null when it is not one. */
export function cleanUrl(input: string | null | undefined): string | null {
  if (!input) return null;
  let v = input.trim();
  if (!v || /\s/.test(v)) return null;
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(v)) v = `https://${v}`;
  try {
    const u = new URL(v);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname.includes('.')) return null;
    return u.toString().length > 500 ? null : u.toString().replace(/\/$/, u.pathname === '/' && !u.search ? '' : '/');
  } catch {
    return null;
  }
}

export function cleanLinkedin(input: string | null | undefined): string | null {
  const url = cleanUrl(input);
  if (!url) return null;
  const host = new URL(url).hostname.toLowerCase();
  return host === 'linkedin.com' || host.endsWith('.linkedin.com') ? url : null;
}

/** A plausible email address in lower case, or null. */
export function cleanEmail(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.trim().replace(/^mailto:/i, '').toLowerCase();
  return v.length <= 254 && /^[^\s@]+@[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v) ? v : null;
}

const LEGAL_SUFFIX = /[\s,]+(ltd|limited|gmbh|inc|llc|llp|ab|as|a\/s|aps|bv|b\.v|nv|sa|sas|sarl|srl|spa|plc|oy|ag|co|corp|company)\.?$/i;

/** A company name reduced for matching: "Aster Labs Ltd." and "aster labs" match. */
export function nameKey(name: string): string {
  return name.trim().replace(LEGAL_SUFFIX, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function text(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null;
  const s = v.replace(/\s+/g, ' ').trim();
  if (!s || /^(null|n\/a|unknown|none|-)$/i.test(s)) return null;
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

function wholeNumber(v: unknown, min: number, max: number): number | null {
  let n = NaN;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string') {
    // "about 1,200", "~200+", "1.2k": the first number, read with its thousands separators and suffix.
    const t = v.replace(/(\d)[,\s\u00a0](?=\d{3}(\D|$))/g, '$1');
    const m = /(\d+(?:\.\d+)?)\s*(k\b)?/i.exec(t);
    if (m) n = Number(m[1]) * (m[2] ? 1000 : 1);
  }
  if (!Number.isFinite(n)) return null;
  const r = Math.round(n);
  return r < min || r > max ? null : r;
}

export interface RawCandidate {
  name: string;
  domain: string | null;
  reason: string | null;
}

export interface RawPerson {
  name: string;
  title: string | null;
  email: string | null;
  linkedin: string | null;
  source: string | null;
}

export interface RawSource {
  url: string;
  details: string[];
}

export interface RawCompany {
  name: string;
  domain: string | null;
  website: string | null;
  location: string | null;
  industry: string | null;
  /** Models sometimes answer numbers as text ("about 200"); cleanCompanies reads both. */
  staff: number | string | null;
  founded: number | string | null;
  linkedin: string | null;
  description: string | null;
  /** The profile; lists may come back as one separated line. */
  summary?: string | null;
  market?: string | null;
  clients?: string | null;
  strengths?: string[] | string | null;
  lacks?: string[] | string | null;
  why: string | null;
  match: number | string | null;
  people: RawPerson[];
  /** Plain addresses are read too, as pages tied to no detail. */
  sources: (RawSource | string)[];
}

export interface CleanPerson {
  name: string;
  title: string | null;
  email: string | null;
  linkedin: string | null;
  source: string | null;
}

export interface CleanCompany {
  name: string;
  domain: string | null;
  website: string | null;
  location: string | null;
  industry: string | null;
  staff: number | null;
  founded: number | null;
  linkedin: string | null;
  description: string | null;
  summary: string | null;
  market: string | null;
  clients: string | null;
  strengths: string[];
  lacks: string[];
  why: string | null;
  match: number;
  people: CleanPerson[];
  sources: FinderSource[];
}

/** The short title the AI gives a described search: one line, no quotes, no closing full stop. Null when unusable. */
export function cleanTitle(raw: string | null | undefined): string | null {
  const t = (raw ?? '').replace(/\s+/g, ' ').trim().replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').replace(/[.;:,]+$/, '').trim();
  if (t.length < 3 || t.length > 70) return null;
  return t[0]!.toUpperCase() + t.slice(1);
}

/** Candidates from the first call: trimmed, valid, one per company. */
export function cleanCandidates(raw: RawCandidate[], limit: number): RawCandidate[] {
  const seen = new Set<string>();
  const out: RawCandidate[] = [];
  for (const c of raw) {
    const name = text(c.name, 160);
    if (!name) continue;
    const domain = cleanDomain(c.domain);
    const keys = [`n:${nameKey(name)}`, ...(domain ? [`d:${domain}`] : [])];
    if (keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    out.push({ name, domain, reason: text(c.reason, 400) });
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Turns the model's company research into suggestions: trims every value to what a company record accepts,
 * drops what is not a real value, merges repeats and sorts by match, best first.
 */
export function cleanCompanies(raw: RawCompany[], opts: { limit: number; thisYear: number; sources?: FinderSource[] }): CleanCompany[] {
  const seen = new Set<string>();
  const out: CleanCompany[] = [];
  const titles = new Map((opts.sources ?? []).map((s) => [s.url, s.title]));
  for (const c of raw) {
    const name = text(c.name, 160);
    if (!name) continue;
    const website = cleanUrl(c.website);
    const domain = cleanDomain(c.domain) ?? cleanDomain(website);
    const keys = [`n:${nameKey(name)}`, ...(domain ? [`d:${domain}`] : [])];
    if (keys.some((k) => seen.has(k))) continue;
    keys.forEach((k) => seen.add(k));
    const people = mergePeople(c.people ?? [], []);
    const sources: FinderSource[] = [];
    for (const s of c.sources ?? []) {
      const raw = typeof s === 'string' ? { url: s, details: [] } : s;
      const url = cleanUrl(raw.url);
      if (!url) continue;
      const details = FOUND_FIELDS.filter((f) => raw.details?.includes(f));
      // The same page listed twice keeps every detail it was tied to.
      const same = sources.find((x) => x.url === url);
      if (same) same.details = FOUND_FIELDS.filter((f) => same.details?.includes(f) || details.includes(f));
      else if (sources.length < 6) sources.push({ url, title: titles.get(url) ?? null, details });
    }
    const match = wholeNumber(c.match, 0, 100);
    const list = (v: unknown) => (cleanFillValue('company', 'strengths', v, opts.thisYear) as string[] | null) ?? [];
    const strengths = list(c.strengths);
    // A capability is never both: one the company is strong at is no gap.
    const lacks = list(c.lacks).filter((n) => !strengths.some((x) => x.toLowerCase() === n.toLowerCase()));
    out.push({
      name,
      domain,
      website: website ?? (domain ? `https://${domain}` : null),
      location: text(c.location, 120),
      industry: text(c.industry, 80),
      staff: wholeNumber(c.staff, 1, 10_000_000),
      founded: wholeNumber(c.founded, 1500, opts.thisYear),
      linkedin: cleanLinkedin(c.linkedin),
      description: text(c.description, 600),
      summary: cleanFillValue('company', 'summary', c.summary, opts.thisYear) as string | null,
      market: cleanFillValue('company', 'market', c.market, opts.thisYear) as string | null,
      clients: cleanFillValue('company', 'clients', c.clients, opts.thisYear) as string | null,
      strengths,
      lacks,
      why: text(c.why, 600),
      match: match ?? 50,
      people,
      sources,
    });
  }
  return out.sort((a, b) => b.match - a.match).slice(0, opts.limit);
}

function cleanPerson(p: RawPerson): CleanPerson | null {
  const name = text(p.name, 120);
  if (!name) return null;
  return { name, title: text(p.title, 120), email: cleanEmail(p.email), linkedin: cleanLinkedin(p.linkedin), source: cleanUrl(p.source) };
}

/** The same person named twice: the same name, LinkedIn profile or address. */
export function samePerson(a: CleanPerson, b: CleanPerson): boolean {
  const key = (n: string) => n.toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ');
  return key(a.name) === key(b.name) || (!!a.linkedin && a.linkedin === b.linkedin) || (!!a.email && a.email === b.email);
}

/**
 * The people at a suggestion: those of `found` first, then those of `known` it does not name, up to MAX_PEOPLE.
 * Someone named twice is listed once, with any title, address, profile or page the other mention adds.
 */
export function mergePeople(found: RawPerson[], known: CleanPerson[]): CleanPerson[] {
  const out: CleanPerson[] = [];
  for (const p of [...found.map(cleanPerson), ...known]) {
    if (!p) continue;
    const same = out.find((x) => samePerson(x, p));
    if (same) {
      same.title ??= p.title;
      same.email ??= p.email;
      same.linkedin ??= p.linkedin;
      same.source ??= p.source;
    } else if (out.length < MAX_PEOPLE) out.push({ ...p });
  }
  return out;
}

/* ---------- key people at a company already in Clientbook ---------- */

/** A person the key-people look-up proposes for a company already in Clientbook, waiting to be added or dismissed. */
export interface PeopleSuggestion {
  id: string;
  name: string;
  title: string | null;
  email: string | null;
  linkedin: string | null;
  /** The page that names them. */
  source: string | null;
  model: string | null;
  foundAt: string;
}

/** The latest look-up of a company's key people. */
export interface PeopleLookup {
  id: string;
  /** Where this company stands: running until its own look-up is done, even while others in the same run go on. */
  status: SearchStatus;
  /** Other companies looked up in the same run. */
  others: number;
  startedBy: UserRef | null;
  createdAt: string;
  finishedAt: string | null;
  /** Why it stopped, when it failed. */
  error: string | null;
  /** How many new people it proposed. */
  found: number;
}

export interface CompanyPeopleAi {
  lookup: PeopleLookup | null;
  suggestions: PeopleSuggestion[];
}

/** What adding, dismissing or undoing suggested people did, one entry per suggestion. */
export interface PeopleDecision {
  done: { id: string; name: string; personId: string | null }[];
  skipped: { id: string; name: string; reason: string }[];
}

/** The most suggested people one add, dismiss or undo request carries. */
export const PEOPLE_DECIDE_MAX = 50;

/** One company of a key-people look-up, for the run's panel in the AI Finder. */
export interface PeopleRunCompany {
  id: string;
  name: string;
  hue: number;
  logo: string | null;
  /** Gone to the bin or merged away. */
  gone: boolean;
  state: 'waiting' | 'looking' | 'done' | 'failed' | 'stopped';
  found: number;
  /** Found people still waiting to be added or dismissed. */
  toReview: number;
  error: string | null;
}

export interface PeopleRun {
  companies: PeopleRunCompany[];
}

/** "Key people at Tidewell Foods", "Key people at 12 companies". */
export function peopleTitle(names: string[]): string {
  return names.length === 1 ? searchTitle('people', { companyName: names[0] }) : `Key people at ${names.length} companies`;
}

/* ---------- where values came from ---------- */

function pageSource(s: FinderSource): ValueSource {
  return { url: s.url, name: null, title: s.title };
}

/** Where one detail of a suggestion came from: the pages tied to it, or every page the AI read when it tied none. */
export function detailSources(sources: FinderSource[], field: FoundField): { sources: ValueSource[]; broad: boolean } {
  const own = sources.filter((s) => s.details?.includes(field));
  const list = own.length ? own : sources;
  return { sources: list.slice(0, 6).map(pageSource), broad: !own.length && list.length > 0 };
}

/**
 * Where a suggested person came from: the page that names them and their LinkedIn profile, or every page the AI read
 * on the company when it gave neither.
 */
export function personSources(p: { source: string | null; linkedin: string | null }, company: FinderSource[]): { sources: ValueSource[]; broad: boolean } {
  const pages = [...new Set([cleanUrl(p.source), cleanLinkedin(p.linkedin)].filter((u): u is string => !!u))];
  if (pages.length) return { sources: pages.map((url) => ({ url, name: null, title: company.find((s) => s.url === url)?.title ?? null })), broad: false };
  return { sources: company.slice(0, 6).map(pageSource), broad: company.length > 0 };
}

/** A LinkedIn address that points at a person's profile rather than a company page or a search. */
export function cleanLinkedinProfile(input: string | null | undefined): string | null {
  const url = cleanLinkedin(input);
  if (!url) return null;
  return /^\/(in|pub)\/[^/]+/i.test(new URL(url).pathname) ? url : null;
}

function phoneNumber(raw: unknown): string | null {
  const v = text(raw, 40);
  return v && /^[+()\d][\d\s().\-/]{5,}$/.test(v) ? v : null;
}

/** A fill proposal's source as the record keeps it once the value is accepted. */
export function fillSource(v: Pick<FillValue, 'source' | 'sourceUrl'>): ValueSource {
  return { url: v.sourceUrl, name: v.source || null };
}

/** A proposed value in the shape the record stores, or null when it does not fit. */
export function cleanFillValue(target: FillTarget, field: FillField, raw: unknown, thisYear: number): FillFieldValue | null {
  if (!isFillField(target, field)) return null;
  if (target === 'person') {
    switch (field as PersonFillField) {
      case 'title':
        return text(raw, 120);
      case 'linkedin':
        return cleanLinkedinProfile(typeof raw === 'string' ? raw : null);
      case 'location':
        return text(raw, 120);
      case 'phone':
        return phoneNumber(raw);
    }
  }
  switch (field as CompanyFillField) {
    case 'domain':
      return cleanDomain(typeof raw === 'string' ? raw : null);
    case 'website':
      return cleanUrl(typeof raw === 'string' ? raw : null);
    case 'linkedin':
      return cleanLinkedin(typeof raw === 'string' ? raw : null);
    case 'staff':
      return wholeNumber(raw, 1, 10_000_000);
    case 'founded':
      return wholeNumber(raw, 1500, thisYear);
    case 'industry':
      return text(raw, 80);
    case 'location':
      return text(raw, 120);
    case 'phone':
      return phoneNumber(raw);
    case 'description':
      return text(raw, 1000);
    case 'summary':
      return text(raw, PROFILE_LIMITS.summary);
    case 'market':
      return text(raw, PROFILE_LIMITS.market);
    case 'clients':
      return text(raw, PROFILE_LIMITS.clients);
    case 'strengths':
    case 'lacks': {
      const items = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split(/[;,\n]/) : [];
      const names = normaliseTerms(items.map((v) => text(v, 200)).filter((v): v is string => !!v && v.length <= PROFILE_LIMITS.capability));
      return names.length ? names.slice(0, 8) : null;
    }
  }
}

/** True when two detail values are the same, lists compared item by item. */
export function sameFillValue(a: FillFieldValue | null | undefined, b: FillFieldValue | null | undefined): boolean {
  const flat = (v: FillFieldValue | null | undefined) => (Array.isArray(v) ? v.join('\u0000') : String(v ?? ''));
  return flat(a) === flat(b);
}

/** The fields a record has no value for, among the ones asked for. */
export function recordGaps<F extends FillField>(record: Partial<Record<FillField, unknown>>, fields: readonly F[]): F[] {
  return fields.filter((f) => {
    const v = record[f];
    return v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
  });
}

/** Sorts the people at a company into those a lookup covers and those it skips, and why. */
export function companyFillPlan(people: Pick<FinderPersonOption, 'id' | 'identifiable' | 'gaps'>[], fields: readonly PersonFillField[]): CompanyFillPlan {
  const plan: CompanyFillPlan = { lookups: [], complete: 0, unidentifiable: 0 };
  for (const p of people) {
    if (!p.gaps.some((g) => fields.includes(g))) plan.complete += 1;
    else if (!p.identifiable) plan.unidentifiable += 1;
    else plan.lookups.push(p.id);
  }
  return plan;
}

/**
 * "Looking up details for 3 people. Skipped 2 people who already had every chosen detail and 1 with nothing to identify
 * by.": what a lookup on a company's people covers and leaves out.
 */
export function companyFillText(plan: Pick<CompanyFillPlan, 'complete' | 'unidentifiable'> & { lookedUp: number }): string {
  const head = `Looking up details for ${recordCount('person', plan.lookedUp)}`;
  const parts = [
    plan.complete ? { n: plan.complete, why: 'who already had every chosen detail' } : null,
    plan.unidentifiable ? { n: plan.unidentifiable, why: 'with nothing to identify them by' } : null,
  ].filter((x): x is { n: number; why: string } => !!x);
  if (!parts.length) return head;
  const said = parts.map((p, i) => `${i === 0 ? plural(p.n, 'person', 'people') : formatNumber(p.n)} ${p.why}`);
  return `${head}. Skipped ${said.join(' and ')}.`;
}

/** "1 company", "3 people". */
export function recordCount(target: FillTarget, n: number): string {
  if (target === 'person') return n === 1 ? '1 person' : `${n} people`;
  return n === 1 ? '1 company' : `${n} companies`;
}

/** The details a suggestion gives that the company has no value for, in the order the Finder lists them. */
export function addableDetails(suggestion: Partial<Record<FoundField, unknown>>, company: Partial<Record<FoundField, unknown>>): FoundField[] {
  const filled = FOUND_FIELDS.filter((f) => !recordGaps(suggestion, [f]).length);
  return recordGaps(company, filled);
}

/** "2 people and 3 details", "1 person", "1 detail": what adding to a company brings, for buttons and messages. */
export function addedWords(people: number, details: number): string {
  return [people ? plural(people, 'person', 'people') : null, details ? plural(details, 'detail') : null].filter(Boolean).join(' and ');
}

/* ---------- duplicates ---------- */

export interface DuplicateHit {
  id: string;
  name: string;
  reason: 'domain' | 'name' | 'email';
}

/**
 * How a suggestion relates to what is already in Clientbook: the same domain means it is already there,
 * the same name only means it may be.
 */
export function classifyDuplicate(hits: DuplicateHit[]): { id: string; name: string; reason: 'domain' | 'name' } | null {
  const domain = hits.find((h) => h.reason === 'domain');
  if (domain) return { id: domain.id, name: domain.name, reason: 'domain' };
  const name = hits.find((h) => h.reason === 'name');
  return name ? { id: name.id, name: name.name, reason: 'name' } : null;
}

/** Which person on record a suggested person matches: the same email anywhere wins over the same name at the company. */
export function pickPersonMatch(hits: DuplicateHit[]): { id: string; name: string } | null {
  const hit = hits.find((h) => h.reason === 'email') ?? hits.find((h) => h.reason === 'name') ?? null;
  return hit ? { id: hit.id, name: hit.name } : null;
}

/* ---------- small formatting helpers ---------- */

/** Ends a message with a full stop, so whatever follows reads as its own sentence. */
export function sentence(text: string): string {
  const t = text.trim();
  return !t || /[.!?…]$/.test(t) ? t : `${t}.`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** "approved by Priya 18 Jul", the source detail on records an approval creates, dated by the day in `timeZone`. */
export function approvalDetail(name: string, at: Date, timeZone: string): string {
  const day = zonedParts(at, timeZone);
  return `approved by ${firstName(name)} ${day.day} ${MONTHS[day.month - 1]}`;
}

/** A short title for a search, from what it looks for. */
export function searchTitle(mode: FinderMode, input: { query?: string; companyName?: string; filters?: Partial<FilterParams>; companies?: number; people?: number }): string {
  if (mode === 'like') return `Companies like ${input.companyName ?? 'a company'}`;
  if (mode === 'people') return `Key people at ${input.companyName ?? 'a company'}`;
  if (mode === 'fill') {
    if (!input.people) return `Fill details for ${recordCount('company', input.companies ?? 0)}`;
    return `Fill details for ${recordCount('person', input.people)}${input.companyName ? ` at ${input.companyName}` : ''}`;
  }
  if (mode === 'filters') {
    const f = input.filters ?? {};
    const band = STAFF_BANDS.find((b) => b.id === f.staff);
    const where = [f.industry?.trim(), f.location?.trim() ? `in ${f.location.trim()}` : ''].filter(Boolean).join(' ');
    const founded =
      f.foundedFrom && f.foundedTo
        ? `founded ${f.foundedFrom}–${f.foundedTo}`
        : f.foundedFrom
          ? `founded ${f.foundedFrom} or later`
          : f.foundedTo
            ? `founded by ${f.foundedTo}`
            : '';
    const t = [where, band ? `${band.label} staff` : '', founded, f.keywords?.trim() ?? ''].filter(Boolean).join(', ') || 'Filtered search';
    const cut = t.length > 80 ? `${t.slice(0, 79).trimEnd()}…` : t;
    return cut[0]!.toUpperCase() + cut.slice(1);
  }
  const q = (input.query ?? '').replace(/\s+/g, ' ').trim();
  if (q.length <= 60) return q;
  const cut = q.slice(0, 60);
  const space = cut.lastIndexOf(' ');
  return `${(space > 30 ? cut.slice(0, space) : cut).replace(/[,.;:]+$/, '')}…`;
}
